import { Box, Button, Typography, CircularProgress, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { WebSocketCallServiceConfig } from '@shared/services/websocketCallService';
import { ConversationListMessage } from '@widgets/Popup/MonitorCampaignV2/ConversationListMessage';

import { useWebSocketCallService, WebSocketTranscript } from '../../hooks/useWebSocketCallService';
import { RootState, useAppSelector } from '@shared/store';
import { STOP_AUTOSCROLL_THRESHOLD_PX } from './constants';
import { useTranslation } from 'react-i18next';
import { CopyCallIdButton } from '@shared/components/CopyCallIdButton';
import { CallActors } from '@shared/constants/recipient-status.constants';

const getTranscriptInterval = (
  currentMessage: WebSocketTranscript,
  previousMessage?: WebSocketTranscript,
): number | undefined => {
  if (
    typeof currentMessage.firstReceivedTime !== 'number' ||
    typeof previousMessage?.lastReceivedTime !== 'number'
  ) {
    return undefined;
  }

  return Math.max(
    0,
    Math.floor((currentMessage.firstReceivedTime - previousMessage.lastReceivedTime) / 1000),
  );
};

interface WebSocketCallComponentProps {
  serviceUrl: string;
  campaignId: string;
  customerName?: string;
  onCallEnd?: () => void;
  setIsWSCallInProgress: (value: React.SetStateAction<boolean>) => void;
  setIsWSConnecting?: (value: React.SetStateAction<boolean>) => void;
}

export const WebSocketCallComponent: React.FC<WebSocketCallComponentProps> = ({
  serviceUrl,
  campaignId,
  customerName,
  onCallEnd,
  setIsWSCallInProgress,
  setIsWSConnecting,
}) => {
  const theme = useTheme();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const { t } = useTranslation();
  const [isConnecting, setIsConnecting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);
  const isPinnedToBottomRef = useRef(true);

  // Name shown for the customer side of the conversation (and sent to the backend)
  const displayName = useMemo(() => {
    const fullName = [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return customerName?.trim() || fullName || 'Anonymous';
  }, [customerName, currentUser]);

  // Build service config once
  const config: WebSocketCallServiceConfig = useMemo(() => {
    return {
      serviceUrl,
      campaignId,
      customerName: displayName,

      // Lifecycle callbacks as onConnectedToServer, onSessionReady, onTranscriptReceived could be placed here (service handles audio internally)
      onDisconnect: () => {
        onCallEnd?.();
      },
    };
  }, [serviceUrl, campaignId, displayName, onCallEnd]);

  const {
    isConnected,
    isSessionReady,
    callId,
    error,
    transcripts,
    startCall,
    endCall,
    disconnect,
  } = useWebSocketCallService(config);

  // Mirror "in progress" flag to parent
  useEffect(() => {
    setIsWSCallInProgress(isConnected);
  }, [isConnected, setIsWSCallInProgress]);

  // Mirror "connecting" flag to parent
  useEffect(() => {
    setIsWSConnecting?.(isConnecting);

    // Cleanup: reset connecting state when component unmounts
    return () => {
      setIsWSConnecting?.(false);
    };
  }, [isConnecting, setIsWSConnecting]);

  // Start the call: connect WS + init audio + stream
  const handleStartCall = async () => {
    try {
      setIsConnecting(true);
      await startCall();
    } catch (e) {
      console.error('Failed to start call:', e);
    } finally {
      setIsConnecting(false);
    }
  };

  // End the call and cleanup
  const handleEndCall = async () => {
    try {
      await endCall();
    } finally {
      disconnect();
      onCallEnd?.();
    }
  };

  const handleMessagesScroll = useCallback(() => {
    if (!messagesBoxRef.current) return;

    const { clientHeight, scrollTop, scrollHeight } = messagesBoxRef.current;
    isPinnedToBottomRef.current =
      scrollHeight - clientHeight - scrollTop < STOP_AUTOSCROLL_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (!messagesEndRef.current || !isPinnedToBottomRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
  }, [transcripts]);

  return (
    <Box
      sx={{
        mb: '14px',
        display: 'inline-flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
      {/* Status — only after session.ready so "Connected" is not shown on socket-open alone. */}
      {isSessionReady && callId ? (
        <Box sx={{ mb: 2, alignSelf: 'center' }}>
          <Typography data-testid="ws-call-connection-status">
            {t('webSocketCall.connection')}:{' '}
            {isConnected ? t('webSocketCall.connected') : t('webSocketCall.disconnected')}
          </Typography>
          <Typography data-testid="ws-call-session-status">
            {t('webSocketCall.session')}: {t('webSocketCall.ready')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CopyCallIdButton callId={callId} showLabel />
          </Box>
          {error && (
            <Typography color="error">
              {t('webSocketCall.error')}: {error}
            </Typography>
          )}
        </Box>
      ) : null}

      {/* Controls — End Call only after session.ready (socket open ≠ session). */}
      <Box sx={{ mb: 2, alignSelf: 'center' }}>
        {!isConnected ? (
          <Button
            data-testid="ws-call-start"
            sx={{
              minWidth: 200,
              textTransform: 'none',
              fontWeight: 600,
            }}
            variant="contained"
            color="success"
            size="large"
            onClick={handleStartCall}
            disabled={isConnecting}>
            {isConnecting ? t('webSocketCall.connecting') : t('webSocketCall.startCall')}
          </Button>
        ) : null}
        {isSessionReady ? (
          <Button
            data-testid="ws-call-end"
            sx={{ mt: 2 }}
            onClick={handleEndCall}
            variant="contained"
            color="error"
            size="large">
            {t('webSocketCall.endCall')}
          </Button>
        ) : null}
      </Box>

      {/* Loading State - Connected but waiting for session.ready */}
      {isConnected && !isSessionReady && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>{t('webSocketCall.joiningSession')}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('webSocketCall.establishingSession')}
          </Typography>
        </Box>
      )}

      {/* Transcript */}
      {isSessionReady && (
        <Box sx={{ mt: 2, padding: '0 20px' }}>
          <Typography>{t('webSocketCall.liveTranscript')}</Typography>
          <Box
            data-testid="ws-call-transcript"
            sx={{
              border: `1px solid ${theme.palette.border?.default ?? theme.palette.divider}`,
              p: 1.5,
              minHeight: 200,
              maxHeight: 400,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
            }}
            ref={messagesBoxRef}
            onScroll={handleMessagesScroll}>
            <Box sx={{ width: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
              {transcripts && transcripts.length > 0
                ? transcripts.map((messageItem, idx) => (
                    <ConversationListMessage
                      key={messageItem.id ?? idx}
                      actorName={
                        messageItem.actor === CallActors.AGENT
                          ? t('webSocketCall.agentActorName')
                          : displayName
                      }
                      messageItem={messageItem}
                      isStacked={false}
                      timeSincePreviousMessage={getTranscriptInterval(
                        messageItem,
                        transcripts[idx - 1],
                      )}
                    />
                  ))
                : t('webSocketCall.waitingForTranscript')}
              <div ref={messagesEndRef} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
